// src/services/order.service.js
import api from './api';

const orderService = {
  // ── SO: Create order ──────────────────────────────────────────────────────
  createOrder: async (orderData) => {
    try {
      const response = await api.post('/orders', orderData);
      return response.data;
    } catch (error) {
      console.error('Error creating order:', error);
      throw error;
    }
  },

  // ── Get orders (role-filtered by backend) ─────────────────────────────────
  // companyId is only sent for global roles (SUPER_ADMIN / OFFICE_ADMIN).
  // Company-scoped roles call getOrders() or getOrders(status) with no companyId,
  // so the backend keeps resolving their companyId from the JWT — unchanged.
  getOrders: async (status = null, companyId = undefined) => {
    try {
      const params = {};
      if (status) params.status = status;
      if (companyId) params.companyId = companyId;   // ✅ forward for global roles
      const response = await api.get('/orders', { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching orders:', error);
      throw error;
    }
  },

  // ── Get orders by godown (GM) ─────────────────────────────────────────────
  getOrdersByGodown: async (godownId, status = null) => {
    try {
      const params = {};
      if (status) params.status = status;
      const response = await api.get(`/orders/godown/${godownId}`, { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching orders:', error);
      throw error;
    }
  },

  // ── Get single order ──────────────────────────────────────────────────────
  getOrderById: async (id) => {
    try {
      const response = await api.get(`/orders/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching order:', error);
      throw error;
    }
  },

  // ── Executive: Confirm order ──────────────────────────────────────────────
  confirmOrder: async (id) => {
    try {
      const response = await api.post(`/orders/${id}/confirm`);
      return response.data;
    } catch (error) {
      console.error('Error confirming order:', error);
      throw error;
    }
  },

  // ── Executive: Cancel order ───────────────────────────────────────────────
  cancelOrder: async (id) => {
    try {
      const response = await api.post(`/orders/${id}/cancel`);
      return response.data;
    } catch (error) {
      console.error('Error cancelling order:', error);
      throw error;
    }
  },

  // ── GM: Dispatch order items (partial or full) ────────────────────────────
  dispatchOrder: async (id, dispatchData) => {
    try {
      const response = await api.post(`/orders/${id}/dispatch`, dispatchData);
      return response.data;
    } catch (error) {
      console.error('Error dispatching order:', error);
      throw error;
    }
  },

  // ── GM: Update delivery status ────────────────────────────────────────────
  updateDeliveryStatus: async (id, data) => {
    try {
      const response = await api.post(`/orders/${id}/delivery-status`, data);
      return response.data;
    } catch (error) {
      console.error('Error updating delivery status:', error);
      throw error;
    }
  },

  // ── Get dispatch logs for an order ───────────────────────────────────────
  getDispatchLogs: async (id) => {
    try {
      const response = await api.get(`/orders/${id}/dispatch-logs`);
      return response.data;
    } catch (error) {
      console.error('Error fetching dispatch logs:', error);
      throw error;
    }
  },

  // ── Stock management ──────────────────────────────────────────────────────
  getGodownStock: async (godownId) => {
    try {
      const response = await api.get(`/orders/stock/godown/${godownId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching stock:', error);
      throw error;
    }
  },

  updateGodownStock: async (godownId, data) => {
    try {
      const response = await api.post(`/orders/stock/godown/${godownId}`, data);
      return response.data;
    } catch (error) {
      console.error('Error updating stock:', error);
      throw error;
    }
  },

  // ── Get godowns (with pagination) ────────────────────────────────────────
  getGodowns: async (page = 1, limit = 1000, search = '') => {
    try {
      const params = { page, limit };
      if (search) params.search = search;
      const response = await api.get('/godowns', { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching godowns:', error);
      throw error;
    }
  },

  // ── Get products (with pagination + search) ──────────────────────────────
  getProducts: async (companyId, page = 1, limit = 1000, search = '') => {
    try {
      const params = { companyId, page, limit };
      if (search) params.search = search;
      const response = await api.get('/products', { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching products:', error);
      throw error;
    }
  },

  // ── Legacy: kept for backwards compat ────────────────────────────────────
  getOrdersByCompany: async (companyId) => {
    try {
      const params = companyId ? { companyId } : {};
      const response = await api.get('/orders', { params });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  updateOrder: async (id, orderData) => {
    try {
      const response = await api.post(`/orders/${id}`, orderData);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  deleteOrder: async (id) => {
    try {
      const response = await api.delete(`/orders/${id}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  rejectOrder: async (id, data) => {
    try {
      const response = await api.post(`/orders/${id}/reject`, data);
      return response.data;
    } catch (error) {
      console.error('Error rejecting order:', error);
      throw error;
    }
  },
};

export default orderService;